import axiosInstance from '@/app/api/axiosInstance';
import {
  JobCard,
  JobCardCreate,
  JobCardUpdate,
  JobCardListItem,
  JobCardConfig,
  JobCardStatusUpdate,
  JobCardItemUpdate,
  JobCardStatus,
  JobCardPriority,
  JobCardType,
} from '@/app/types/jobCard';
import { PaginatedResponse, ListQueryParams } from '@/app/types/api';

// Helper to parse numeric fields from job card response
const parseJobCardResponse = (jobCard: JobCard): JobCard => ({
  ...jobCard,
  total_amount: jobCard.total_amount ? String(jobCard.total_amount) : '0',
  odometer_reading: jobCard.odometer_reading ? Number(jobCard.odometer_reading) : null,
  items: jobCard.items.map((item) => ({
    ...item,
    quantity: item.quantity ? String(item.quantity) : '0',
    rate: item.rate ? String(item.rate) : '0',
    discount_percent: item.discount_percent ? String(item.discount_percent) : '0',
    discount_amount: item.discount_amount ? String(item.discount_amount) : '0',
    tax_amount: item.tax_amount ? String(item.tax_amount) : '0',
    amount: item.amount ? String(item.amount) : '0',
    total_amount: item.total_amount ? String(item.total_amount) : '0',
    estimated_duration_minutes: item.estimated_duration_minutes
      ? Number(item.estimated_duration_minutes)
      : null,
    actual_duration_minutes: item.actual_duration_minutes
      ? Number(item.actual_duration_minutes)
      : null,
  })),
});

// Helper to parse list item
const parseJobCardListItem = (item: JobCardListItem): JobCardListItem => ({
  ...item,
  total_amount: item.total_amount ? String(item.total_amount) : '0',
});

// Query params interface for job card list
export interface JobCardListQueryParams extends ListQueryParams {
  site_id?: number;
  vehicle_id?: number;
  status?: JobCardStatus;
  lead_technician_id?: number;
  job_card_type?: JobCardType;
  priority?: JobCardPriority;
}

/**
 * Get job card configuration (types, priorities, statuses, checklist items, etc.)
 */
export const getJobCardConfig = async (): Promise<JobCardConfig> => {
  const response = await axiosInstance.get<JobCardConfig>('/job-cards/config');
  return response.data;
};

/**
 * List job cards with pagination and filtering
 */
export const listJobCards = async (
  params: JobCardListQueryParams
): Promise<PaginatedResponse<JobCardListItem>> => {
  const response = await axiosInstance.get<PaginatedResponse<JobCardListItem>>('/job-cards/', {
    params,
  });
  response.data.items = response.data.items.map(parseJobCardListItem);
  return response.data;
};

/**
 * Get a single job card by ID with full details
 */
export const getJobCard = async (id: number): Promise<JobCard> => {
  const response = await axiosInstance.get<JobCard>(`/job-cards/${id}`);
  return parseJobCardResponse(response.data);
};

/**
 * Create a new job card
 */
export const createJobCard = async (data: JobCardCreate): Promise<JobCard> => {
  const payload = {
    ...data,
    // Ensure numeric fields are sent correctly
    odometer_reading: data.odometer_reading ? Number(data.odometer_reading) : null,
    items: data.items?.map((item) => ({
      ...item,
      quantity: String(item.quantity),
      rate: String(item.rate),
      ...(item.discount_percent !== undefined && {
        discount_percent: String(item.discount_percent),
      }),
      ...(item.discount_amount !== undefined && {
        discount_amount: String(item.discount_amount),
      }),
      estimated_duration_minutes: item.estimated_duration_minutes
        ? Number(item.estimated_duration_minutes)
        : null,
    })),
  };

  const response = await axiosInstance.post<JobCard>('/job-cards/', payload);
  return parseJobCardResponse(response.data);
};

/**
 * Update an existing job card
 */
export const updateJobCard = async (id: number, data: JobCardUpdate): Promise<JobCard> => {
  const payload = {
    ...data,
    // Ensure numeric fields are sent correctly
    ...(data.odometer_reading !== undefined && {
      odometer_reading: data.odometer_reading ? Number(data.odometer_reading) : null,
    }),
    ...(data.items && {
      items: data.items.map((item) => ({
        ...item,
        quantity: String(item.quantity),
        rate: String(item.rate),
        ...(item.discount_percent !== undefined && {
          discount_percent: String(item.discount_percent),
        }),
        ...(item.discount_amount !== undefined && {
          discount_amount: String(item.discount_amount),
        }),
        estimated_duration_minutes: item.estimated_duration_minutes
          ? Number(item.estimated_duration_minutes)
          : null,
      })),
    }),
  };

  const response = await axiosInstance.patch<JobCard>(`/job-cards/${id}`, payload);
  return parseJobCardResponse(response.data);
};

/**
 * Update job card status with workflow validation
 */
export const updateJobCardStatus = async (
  id: number,
  statusUpdate: JobCardStatusUpdate
): Promise<JobCard> => {
  const response = await axiosInstance.post<JobCard>(`/job-cards/${id}/status`, statusUpdate);
  return parseJobCardResponse(response.data);
};

/**
 * Update a specific job card item (technician assignment, duration, completion status)
 */
export const updateJobCardItem = async (
  jobCardId: number,
  itemId: number,
  data: JobCardItemUpdate
): Promise<JobCard> => {
  const payload = {
    ...data,
    ...(data.estimated_duration_minutes !== undefined && {
      estimated_duration_minutes: data.estimated_duration_minutes
        ? Number(data.estimated_duration_minutes)
        : null,
    }),
    ...(data.actual_duration_minutes !== undefined && {
      actual_duration_minutes: data.actual_duration_minutes
        ? Number(data.actual_duration_minutes)
        : null,
    }),
  };

  const response = await axiosInstance.patch<JobCard>(
    `/job-cards/${jobCardId}/items/${itemId}`,
    payload
  );
  return parseJobCardResponse(response.data);
};

/**
 * Convert a completed job card to a sales invoice
 * Note: Status must be 'ready' or 'delivered', and job card must have party_id and items
 */
export const convertJobCardToInvoice = async (
  id: number
): Promise<{ id: number; voucher_number: string }> => {
  const response = await axiosInstance.post<{ id: number; voucher_number: string }>(
    `/job-cards/${id}/convert-to-invoice`
  );
  return response.data;
};
